import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import type { EmployeeSessionRow } from '@/lib/hrProfileService';

export interface EmployeeEmployment {
  id: string;
  companyId: string;
  companyName: string;
  status: 'pending_approval' | 'active' | 'terminated';
  jobTitle: string | null;
  startDate: string | null;
  endDate: string | null;
  invitedAt: string | null;
  approvedAt: string | null;
}

export interface EmployeeSession {
  profile: {
    id: string;
    nationalId: string;
    phone: string;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    dateOfBirth: string | null;
    gender: string | null;
    maritalStatus: string | null;
    city: string | null;
    street: string | null;
    houseNumber: string | null;
    postalCode: string | null;
    bankName: string | null;
    bankBranch: string | null;
    bankAccountNumber: string | null;
  };
  employments: EmployeeEmployment[];
  activeEmploymentId: string | null;
}

interface EmployeeUserContextType {
  session: EmployeeSession | null;
  seedSession: (s: EmployeeSession) => void;
  setActiveEmployment: (employmentId: string) => void;
  updateProfile: (updates: Partial<EmployeeSession['profile']>) => void;
  clearSession: () => void;
}

const EmployeeUserContext = createContext<EmployeeUserContextType | undefined>(undefined);

export function buildSessionFromRows(rows: EmployeeSessionRow[]): EmployeeSession | null {
  if (!rows.length) return null;
  const first = rows[0]!;
  const profile: EmployeeSession['profile'] = {
    id: first.profile_id,
    nationalId: first.national_id,
    phone: first.phone,
    email: first.email,
    firstName: first.first_name,
    lastName: first.last_name,
    dateOfBirth: first.date_of_birth,
    gender: first.gender,
    maritalStatus: first.marital_status,
    city: first.city,
    street: first.street,
    houseNumber: first.house_number,
    postalCode: first.postal_code,
    bankName: first.bank_name,
    bankBranch: first.bank_branch,
    bankAccountNumber: first.bank_account_number,
  };

  const employments: EmployeeEmployment[] = rows
    .filter(r => r.employment_id)
    .map(r => ({
      id: r.employment_id!,
      companyId: r.employment_company_id!,
      companyName: r.company_name ?? '',
      status: r.employment_status as EmployeeEmployment['status'],
      jobTitle: r.job_title,
      startDate: r.start_date,
      endDate: r.end_date,
      invitedAt: r.invited_at,
      approvedAt: r.approved_at,
    }));

  const activeEmp = employments.find(e => e.status === 'active')
    ?? employments.find(e => e.status === 'pending_approval')
    ?? employments[0];

  return {
    profile,
    employments,
    activeEmploymentId: activeEmp?.id ?? null,
  };
}

export function EmployeeUserProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<EmployeeSession | null>(null);

  const seedSession = useCallback((s: EmployeeSession) => setSession(s), []);

  const setActiveEmployment = useCallback((employmentId: string) => {
    setSession(prev => {
      if (!prev) return prev;
      const exists = prev.employments.some(e => e.id === employmentId);
      if (!exists) return prev;
      return { ...prev, activeEmploymentId: employmentId };
    });
  }, []);

  const updateProfile = useCallback((updates: Partial<EmployeeSession['profile']>) => {
    setSession(prev => {
      if (!prev) return prev;
      return { ...prev, profile: { ...prev.profile, ...updates } };
    });
  }, []);

  const clearSession = useCallback(() => setSession(null), []);

  return (
    <EmployeeUserContext.Provider value={{
      session,
      seedSession,
      setActiveEmployment,
      updateProfile,
      clearSession,
    }}>
      {children}
    </EmployeeUserContext.Provider>
  );
}

export function useEmployeeUser(): EmployeeUserContextType {
  const ctx = useContext(EmployeeUserContext);
  if (!ctx) throw new Error('useEmployeeUser must be used inside EmployeeUserProvider');
  return ctx;
}
